import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";

export const StripchatContext = createContext();

const STRIPCHAT_API = "https://go.mavrtracktor.com/app/models-ext/models";
const STRIPCHAT_KEY = "948255b561e663597d96";
const REFRESH_INTERVAL = 30000; // 30 seconds per StripCash docs

// ─── Map Stripchat tags → our URL category slugs ─────────────────────────────
export const STRIPCHAT_CATEGORY_MAP = {
  "girls/asian":             "asian-cam-girls",
  "girls/ebony":             "ebony-cam-girls",
  "girls/latina":            "latina-cam-girls",
  "girls/blonde":            "blonde-cam-girls",
  "girls/brunette":          "brunette-cam-girls",
  "girls/mature":            "mature-cam-girls",
  "girls/milf":              "milf-cam-girls",
  "girls/teen":              "teen-cam-girls",
  "girls/young":             "teen-cam-girls",
  "girls/bigboobs":          "big-tits-cam-girls",
  "girls/big-tits":          "big-tits-cam-girls",
  "girls/bigass":            "big-ass-cam-girls",
  "girls/squirt":            "squirt-cam-girls",
  "girls/anal":              "anal-cam-girls",
  "girls/feet":              "feet-cam-girls",
  "girls/bdsm":              "bdsm-cam-girls",
  "girls/redhead":           "redhead-cam-girls",
  "girls/tattoo":            "tattoo-cam-girls",
  "girls/college":           "college-cam-girls",
  "girls/natural":           "natural-tits-cam-girls",
  "girls/toys":              "toys-cam-girls",
  "girls/dominant":          "dominant-cam-girls",
  "girls/submissive":        "submissive-cam-girls",
  "girls/masturbation":      "masturbation-cams",
  "girls/striptease":        "striptease-cams",
  "girls/lovense":           "lovense-cams",
  "girls/interactive-toys":  "lovense-cams",
  "girls/oil-show":          "oil-show-cams",
  "girls/dirty-talk":        "dirty-talk-cams",
  "girls/blowjob":           "blowjob-cams",
  "girls/deepthroat":        "deepthroat-cams",
  "girls/cowgirl":           "cowgirl-cams",
  "girls/topless":           "topless-cams",
  "girls/twerk":             "twerk-cams",
  "girls/ahegao":            "ahegao-cams",
  "girls/spanish-speaking":  "spanish-cam-girls",
  "couples":                 "couples-cam",
  "couples/anal":            "couples-cam",
  "couples/blowjob":         "blowjob-cams",
};

export const filterStripchat = (performers, filters = {}) => {
  if (!Array.isArray(performers)) return [];

  const safeFilters = {
    ethnicity: [],
    age: [],
    fetishes: [],
    searchTerm: '',
    ...filters
  };

  return performers.filter(performer => {
    const hasActiveFilters = Object.values(safeFilters).some(v =>
      Array.isArray(v) ? v.length > 0 : false
    );
    const hasSearchTerm = safeFilters.searchTerm?.trim().length > 0;

    if (!hasActiveFilters && !hasSearchTerm) return true;

    if (hasSearchTerm) {
      const terms = safeFilters.searchTerm.toLowerCase().split(/[\s,]+/).filter(Boolean);
      const SYNONYMS = {
        'latin': 'latina', 'latina': 'latina', 'latino': 'latina',
        'black': 'ebony', 'african': 'ebony',
        'japanese': 'asian', 'korean': 'asian', 'chinese': 'asian',
        'blonde': 'blonde', 'brunette': 'brunette', 'redhead': 'redhead',
        'slim': 'slim', 'skinny': 'slim', 'thick': 'curvy', 'fit': 'athletic',
        'teen': 'teen', 'young': 'teen', 'milf': 'milf', 'mature': 'mature',
        'squirt': 'squirt', 'feet': 'feet', 'bdsm': 'bdsm', 'anal': 'anal',
      };
      const name   = (performer.displayName || '').toLowerCase();
      const tags   = (performer.tags || []).map(t => t.toLowerCase());
      const allText = [name, ...tags].join(' ');

      const score = terms.reduce((s, t) => {
        const syn = SYNONYMS[t] || t;
        if (name.includes(t)) return s + 10;
        if (tags.some(tg => tg.includes(syn) || tg.includes(t))) return s + 7;
        if (allText.includes(t)) return s + 3;
        return s;
      }, 0);

      if (score === 0) return false;
    }

    const checks = {
      fetishes: !safeFilters.fetishes?.length ||
        safeFilters.fetishes.some(fetish =>
          performer.tags?.some(t => t?.toLowerCase().includes(fetish.toLowerCase()))
        ),
    };

    return Object.values(checks).every(r => r);
  });
};

export const StripchatProvider = ({ children }) => {
  const [performers, setPerformers]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const intervalRef                   = useRef(null);

  const fetchPerformers = async () => {
    try {
      const response = await axios.get(STRIPCHAT_API, {
        headers: { Authorization: `Bearer ${STRIPCHAT_KEY}` },
        params:  { limit: 100 },
        timeout: 10000,
      });

      const models = response.data?.models || [];
      if (!Array.isArray(models) || models.length === 0) {
        setLoading(false);
        return;
      }

      const mapped = models
        .filter(m => m.status === 'public')
        .map(m => {
          const tags = Array.isArray(m.tags) ? m.tags : [];
          const mappedCategories = tags
            .map(t => STRIPCHAT_CATEGORY_MAP[t])
            .filter(Boolean);

          return {
            uniqueModelId:        `sc_${m.id}`,
            performerId:          String(m.id),
            displayName:          m.username,
            profilePictureUrl:    m.snapshotUrl || m.popularSnapshotUrl || m.avatarUrl || '',
            profilePictureFullUrl:m.popularSnapshotUrl || m.snapshotUrl || m.avatarUrl || '',
            chatRoomUrl:          m.clickUrl, // pre-built affiliate tracking URL
            ethnicity:            m.modelsCountry || 'N/A',
            age:                  'N/A',
            tags,
            fetishes:             tags,
            details:              { willingnesses: tags },
            viewers:              m.viewersCount || 0,
            source:               'stripchat',
            mappedCategories,
            isHD:                 !!m.broadcastHD,
            isVR:                 !!m.broadcastVR,
            geobans:              m.geobans || {},
            goalMessage:          m.goalMessage || '',
            isNew:                false,
            country:              m.modelsCountry || '',
          };
        });

      setPerformers(mapped);
      setLoading(false);

      if (mapped.length > 0) {
        const API_URL = process.env.REACT_APP_API_URL || "https://api.instacamgirls.com";
        const batch = mapped.slice(0, 100).map(p => ({
          name: p.displayName,
          displayName: p.displayName,
          network: "stripchat",
          profilePictureUrl: p.profilePictureUrl,
          chatRoomUrl: p.chatRoomUrl,
          fetishes: p.tags || [],
          ethnicity: p.country || "",
          age: null,
          hdStream: p.isHD || false,
        }));
        axios.post(`${API_URL}/api/performers/upsert`, { performers: batch }).catch(() => {});
      }
    } catch (err) {
      console.error("Error fetching Stripchat performers:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformers();
    intervalRef.current = setInterval(fetchPerformers, REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <StripchatContext.Provider value={{ performers, loading }}>
      {children}
    </StripchatContext.Provider>
  );
};
